/**
 * usePermissions - Composable for permission-based access control
 *
 * Provides a unified `can / canAny / canAll` API plus role-flag and
 * convenience computeds for checking user permissions in components.
 * Delegates to `useAuth().hasPermission()` for the underlying check.
 *
 * Fail-mode: **closed**. `hasPermission` returns `false` when
 * `currentUser` is `null` (not yet loaded, expired token, etc.).
 * Counterpart to `useEntitlements.hasFeature()` which fails **open** —
 * subscription outages should never lock users out, but RBAC defaults
 * to least-privilege so an unloaded permission claim hides the action.
 *
 * Convention: core + va perms use dot-style (`bots.view`, `kb.manage`);
 * assist + fax perms use colon-style (`assist:config:read`,
 * `fax:send`). The string form is what `useAuth.hasPermission` checks
 * against the JWT claim verbatim — both forms work side-by-side.
 *
 * Bubble logic: `useAuth.hasPermission` treats `platform.manage` as a
 * superset of every other perm. Assist/fax don't have a comparable
 * super-perm in their namespace — the migration grants their *_admin
 * role every member perm explicitly instead.
 *
 * Per-portal usage pattern:
 *   const { canSendFax, isFaxAdmin } = usePermissions()
 *   if (canSendFax.value) showSendButton()
 *
 *   // Or for router meta.requiredPermissions:
 *   meta: { requiredPermissions: ['fax:send'] }
 *
 *
 * Permission catalog reference (seeded by alembic
 * 0003_seed_system_data + 0009_rbac_fax_catalog):
 *
 * Platform (1)
 *   platform.manage — supercedes every other perm via hasPermission()
 *
 * Partners (2)
 *   partners.view, partners.manage
 *
 * Tenants (3)
 *   tenants.view, tenants.manage, tenants.provision
 *
 * Users / Roles (4)
 *   users.view, users.manage, users.view_all, roles.manage
 *
 * Bots (6)         bots.view|create|edit|delete|deploy|test
 * Knowledge (4)    kb.view|create|edit|delete
 * Conversations(3) conversations.view|view_all|export
 * Analytics (2)    analytics.view|export
 * Integrations (2) integrations.view|manage
 * Billing (2)      billing.view|manage
 * Settings (2)     settings.view|manage
 * Audit (1)        audit.view
 *
 * Tools (5)        tools.view|create|edit|delete|manage
 * Agents (8)       agents.view|manage|admin|monitor,
 *                  queues.view|manage,
 *                  work_items.view|manage
 *
 * JOGS (~43)       contacts.*, segments.*, campaigns.*, consent.*,
 *                  templates.*, compliance.*, call_tracking.*,
 *                  sentiment.*, programs.*, dashboard.*, reports.*,
 *                  predictions.*, crm.*, visitors.view,
 *                  conversions.*, links.*, ambassadors.*,
 *                  messaging_policy.*, presets.*
 *
 * Assist (13, colon-style)
 *   assist:config:read|write
 *   assist:analytics:read
 *   assist:conversations:read|write
 *   assist:knowledge:read|write
 *   assist:setup:read|write
 *   assist:ccai:read|write
 *   assist:prompts:read|write
 *
 * Fax (19, colon-style — Phase 2.23.a)
 *   fax:inbox:read, fax:outbox:read
 *   fax:send, fax:batch:send
 *   fax:numbers:read|write
 *   fax:templates:read|write
 *   fax:workflows:read|write
 *   fax:forms:read|write
 *   fax:analytics:read
 *   fax:contacts:read|write
 *   fax:settings:read|write
 *   fax:api_keys:read|write
 *
 * Channels (8, colon-style — Phase 2.28.h, alembic 0011)
 *   channels:assignments:read|write   — CRUD ChannelAssignment across all types
 *   channels:numbers:read|write       — phone-number inventory + provisioning
 *   channels:settings:read|write      — voice / sms / outbound / email settings
 *   channels:webhooks:read            — webhook config + delivery-log viewing
 *   channels:analytics:read           — channel-level delivery / inbound stats
 */
import { computed } from 'vue';
import { useAuthStore } from '../stores/auth';

export function usePermissions() {
  // Phase 2.23 hotfix — read from the Pinia auth store (the actual
  // source of truth populated by App.vue's authStore.fetchUser()).
  // The previous wiring delegated to useAuth().currentUser, which is
  // a separate module-level ref that nothing in the app ever populates
  // — every `can()` returned false because currentUser stayed null.
  const authStore = useAuthStore();
  const currentUser = computed(() => authStore.user);
  const userPermissions = computed(() => authStore.user?.permissions || []);

  const hasPermission = (permission: string): boolean => {
    if (!authStore.user) return false;
    const perms = authStore.user.permissions || [];
    return perms.includes(permission) || perms.includes('platform.manage');
  };
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some((p) => hasPermission(p));
  };
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every((p) => hasPermission(p));
  };

  /**
   * Check if user has a specific permission
   * @param permission - Permission code (e.g., 'bots.create')
   */
  const can = (permission: string): boolean => {
    return hasPermission(permission);
  };

  /**
   * Check if user has any of the specified permissions
   * @param permissions - Array of permission codes
   */
  const canAny = (permissions: string[]): boolean => {
    return hasAnyPermission(permissions);
  };

  /**
   * Check if user has all of the specified permissions
   * @param permissions - Array of permission codes
   */
  const canAll = (permissions: string[]): boolean => {
    return hasAllPermissions(permissions);
  };

  // Computed permission checks for common operations
  const canManageUsers = computed(() => can('users.manage'));
  const canManageRoles = computed(() => can('roles.manage'));
  const canViewUsers = computed(() => canAny(['users.view', 'users.manage', 'users.view_all']));

  const canManageBots = computed(() => canAny(['bots.create', 'bots.edit', 'bots.delete']));
  const canDeployBots = computed(() => can('bots.deploy'));
  const canViewBots = computed(() => canAny(['bots.view', 'bots.create', 'bots.edit']));

  const canManageKnowledgeBases = computed(() => canAny(['kb.create', 'kb.edit', 'kb.delete']));
  const canViewKnowledgeBases = computed(() => canAny(['kb.view', 'kb.create', 'kb.edit']));

  const canViewConversations = computed(() => canAny(['conversations.view', 'conversations.view_all']));
  const canExportConversations = computed(() => can('conversations.export'));

  const canViewAnalytics = computed(() => can('analytics.view'));
  const canExportAnalytics = computed(() => can('analytics.export'));

  const canManageIntegrations = computed(() => can('integrations.manage'));
  const canViewIntegrations = computed(() => canAny(['integrations.view', 'integrations.manage']));

  const canManageSettings = computed(() => can('settings.manage'));
  const canViewSettings = computed(() => canAny(['settings.view', 'settings.manage']));

  const canManageBilling = computed(() => can('billing.manage'));
  const canViewBilling = computed(() => canAny(['billing.view', 'billing.manage']));

  const canViewAuditLogs = computed(() => can('audit.view'));

  const canManageTenants = computed(() => can('tenants.manage'));
  const canProvisionTenants = computed(() => can('tenants.provision'));
  const canViewTenants = computed(() => canAny(['tenants.view', 'tenants.manage']));

  const canManagePartners = computed(() => can('partners.manage'));
  const canViewPartners = computed(() => canAny(['partners.view', 'partners.manage']));

  const isPlatformAdmin = computed(() => can('platform.manage'));

  /**
   * Get user's role codes
   */
  const userRoles = computed(() => currentUser.value?.roles || []);

  /**
   * Check if user has a specific role
   * @param roleCode - Role code (e.g., 'tenant_admin')
   */
  const hasRole = (roleCode: string): boolean => {
    return userRoles.value.includes(roleCode);
  };

  /**
   * Check if user is a super admin
   */
  const isSuperAdmin = computed(() => hasRole('super_admin') || isPlatformAdmin.value);

  /**
   * Check if user is a partner admin
   */
  const isPartnerAdmin = computed(() => hasRole('partner_admin'));

  /**
   * Check if user is a tenant admin
   */
  const isTenantAdmin = computed(() => hasRole('tenant_admin'));

  /**
   * Check if user is a bot admin
   */
  const isBotAdmin = computed(() => hasRole('bot_admin'));

  /**
   * Check if user is an analyst
   */
  const isAnalyst = computed(() => hasRole('analyst'));

  /**
   * Check if user is a viewer
   */
  const isViewer = computed(() => hasRole('viewer'));

  /**
   * Check if user is a VA agent (front-line agent handling customers).
   */
  const isAgent = computed(() => hasRole('agent'));

  /**
   * Check if user is a VA supervisor (oversees agents and queues).
   */
  const isSupervisor = computed(() => hasRole('supervisor'));

  // ---------------------------------------------------------------------------
  // Assist (Phase 2.23.c — promoted from agent-assist-portal's assistConfigStore
  // marker pattern). The 13 assist:* perms are scoped behind the assist
  // solution gate at the gateway; these computeds are just the user-side check.
  // ---------------------------------------------------------------------------

  const isAssistAdmin = computed(() => hasRole('assist_admin'));
  const isAssistSupervisor = computed(() => hasRole('assist_supervisor'));
  const isAssistAgent = computed(() => hasRole('assist_agent'));

  const canConfigureAssist = computed(() => can('assist:config:write'));
  const canViewAssistConfig = computed(() => canAny(['assist:config:read', 'assist:config:write']));
  const canViewAssistAnalytics = computed(() => can('assist:analytics:read'));
  const canViewAssistConversations = computed(() => canAny(['assist:conversations:read', 'assist:conversations:write']));
  const canWriteAssistConversations = computed(() => can('assist:conversations:write'));
  const canViewAssistKnowledge = computed(() => canAny(['assist:knowledge:read', 'assist:knowledge:write']));
  const canManageAssistKnowledge = computed(() => can('assist:knowledge:write'));
  const canViewAssistSetup = computed(() => canAny(['assist:setup:read', 'assist:setup:write']));
  const canManageAssistSetup = computed(() => can('assist:setup:write'));
  const canManageCCAI = computed(() => can('assist:ccai:write'));
  const canManageAssistPrompts = computed(() => can('assist:prompts:write'));

  // ---------------------------------------------------------------------------
  // Fax (Phase 2.23.a — seeded by alembic 0009_rbac_fax_catalog).
  // 19 perms split across day-to-day (operator) and infra (admin-only).
  // ---------------------------------------------------------------------------

  const isFaxAdmin = computed(() => hasRole('fax_admin'));
  const isFaxOperator = computed(() => hasRole('fax_operator'));

  const canViewFaxInbox = computed(() => can('fax:inbox:read'));
  const canViewFaxOutbox = computed(() => can('fax:outbox:read'));
  const canSendFax = computed(() => can('fax:send'));
  const canBatchSendFax = computed(() => can('fax:batch:send'));
  const canViewFaxNumbers = computed(() => canAny(['fax:numbers:read', 'fax:numbers:write']));
  const canManageFaxNumbers = computed(() => can('fax:numbers:write'));
  const canViewFaxTemplates = computed(() => canAny(['fax:templates:read', 'fax:templates:write']));
  const canManageFaxTemplates = computed(() => can('fax:templates:write'));
  const canViewFaxWorkflows = computed(() => canAny(['fax:workflows:read', 'fax:workflows:write']));
  const canManageFaxWorkflows = computed(() => can('fax:workflows:write'));
  const canViewFaxForms = computed(() => canAny(['fax:forms:read', 'fax:forms:write']));
  const canManageFaxForms = computed(() => can('fax:forms:write'));
  const canViewFaxAnalytics = computed(() => can('fax:analytics:read'));
  const canViewFaxContacts = computed(() => canAny(['fax:contacts:read', 'fax:contacts:write']));
  const canManageFaxContacts = computed(() => can('fax:contacts:write'));
  const canViewFaxSettings = computed(() => canAny(['fax:settings:read', 'fax:settings:write']));
  const canManageFaxSettings = computed(() => can('fax:settings:write'));
  const canViewFaxApiKeys = computed(() => canAny(['fax:api_keys:read', 'fax:api_keys:write']));
  const canManageFaxApiKeys = computed(() => can('fax:api_keys:write'));

  // ---------------------------------------------------------------------------
  // Channels (Phase 2.28.h — seeded by alembic 0011_rbac_channels_catalog).
  // Cross-channel admin (voice/sms/web/email/teams/slack/etc.) under a
  // single namespace. Existing tenant_admin and bot_admin roles inherit the
  // full set via the migration, so legacy tenants don't lose access.
  // ---------------------------------------------------------------------------

  const isChannelAdmin = computed(() => hasRole('channel_admin'));
  const isChannelOperator = computed(() => hasRole('channel_operator'));

  const canViewChannelAssignments = computed(() => canAny(['channels:assignments:read', 'channels:assignments:write']));
  const canManageChannelAssignments = computed(() => can('channels:assignments:write'));
  const canViewChannelNumbers = computed(() => canAny(['channels:numbers:read', 'channels:numbers:write']));
  const canManageChannelNumbers = computed(() => can('channels:numbers:write'));
  const canViewChannelSettings = computed(() => canAny(['channels:settings:read', 'channels:settings:write']));
  const canManageChannelSettings = computed(() => can('channels:settings:write'));
  const canViewChannelWebhooks = computed(() => can('channels:webhooks:read'));
  const canViewChannelAnalytics = computed(() => can('channels:analytics:read'));

  return {
    // Core permission checks
    can,
    canAny,
    canAll,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // All permissions
    permissions: userPermissions,

    // User management
    canManageUsers,
    canManageRoles,
    canViewUsers,

    // Bot management
    canManageBots,
    canDeployBots,
    canViewBots,

    // Knowledge base management
    canManageKnowledgeBases,
    canViewKnowledgeBases,

    // Conversations
    canViewConversations,
    canExportConversations,

    // Analytics
    canViewAnalytics,
    canExportAnalytics,

    // Integrations
    canManageIntegrations,
    canViewIntegrations,

    // Settings
    canManageSettings,
    canViewSettings,

    // Billing
    canManageBilling,
    canViewBilling,

    // Audit
    canViewAuditLogs,

    // Tenants
    canManageTenants,
    canProvisionTenants,
    canViewTenants,

    // Partners
    canManagePartners,
    canViewPartners,

    // Platform
    isPlatformAdmin,

    // Role checks
    userRoles,
    hasRole,
    isSuperAdmin,
    isPartnerAdmin,
    isTenantAdmin,
    isBotAdmin,
    isAnalyst,
    isViewer,
    isAgent,
    isSupervisor,

    // Assist (Phase 2.23.c)
    isAssistAdmin,
    isAssistSupervisor,
    isAssistAgent,
    canConfigureAssist,
    canViewAssistConfig,
    canViewAssistAnalytics,
    canViewAssistConversations,
    canWriteAssistConversations,
    canViewAssistKnowledge,
    canManageAssistKnowledge,
    canViewAssistSetup,
    canManageAssistSetup,
    canManageCCAI,
    canManageAssistPrompts,

    // Fax (Phase 2.23.a)
    isFaxAdmin,
    isFaxOperator,
    canViewFaxInbox,
    canViewFaxOutbox,
    canSendFax,
    canBatchSendFax,
    canViewFaxNumbers,
    canManageFaxNumbers,
    canViewFaxTemplates,
    canManageFaxTemplates,
    canViewFaxWorkflows,
    canManageFaxWorkflows,
    canViewFaxForms,
    canManageFaxForms,
    canViewFaxAnalytics,
    canViewFaxContacts,
    canManageFaxContacts,
    canViewFaxSettings,
    canManageFaxSettings,
    canViewFaxApiKeys,
    canManageFaxApiKeys,

    // Channels (Phase 2.28.h)
    isChannelAdmin,
    isChannelOperator,
    canViewChannelAssignments,
    canManageChannelAssignments,
    canViewChannelNumbers,
    canManageChannelNumbers,
    canViewChannelSettings,
    canManageChannelSettings,
    canViewChannelWebhooks,
    canViewChannelAnalytics,
  };
}
